import React from "react";
import { getUserFacingError } from "../utils/userFacingError";
import localforage from "localforage";
import VpnKeyTwoToneIcon from "@mui/icons-material/VpnKeyTwoTone";
import { enterpriseAPI } from "..";
import { BannerFeedback } from "../components/BannerFeedback";
import "./auth.css";
import { cfg } from "../runtimeConfig";

type TrustedSession = {
  jwt: string;
  verifiedAt: number;
};

const SESSION_TTL_MS = 30 * 60 * 1000; // 30 minutes

// JWT and TrustedSession live in localforage, which is user-writable storage.
// Treat them as untrusted boundary input — validate shape before sending to backend
// or before assuming the cache is meaningful.
export function isJWTStructurallyValid(jwt: unknown): jwt is string {
  if (typeof jwt !== "string") return false;
  const parts = jwt.split(".");
  if (parts.length !== 3) return false;
  try {
    const payload = JSON.parse(atob(parts[1]));
    return typeof payload?.exp === "number";
  } catch {
    return false;
  }
}

// One in-memory revision identifies the account occupying this browser session.
// All auth writes are serialized so an old refresh cannot finish after a new login.
let sessionRevision = 0;
let sessionWrites: Promise<unknown> = Promise.resolve();
let _refreshPromise: Promise<boolean> | null = null;
let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

export const enterpriseSessionRevision = () => sessionRevision;
export const isEnterpriseSessionCurrent = (revision: number) => revision === sessionRevision;

export function writeEnterpriseSession(
  revision: number,
  write: () => Promise<void>,
): Promise<boolean> {
  const next = sessionWrites
    .catch(() => undefined)
    .then(async () => {
      if (!isEnterpriseSessionCurrent(revision)) return false;
      await write();
      return isEnterpriseSessionCurrent(revision);
    });
  sessionWrites = next;
  return next;
}

function beginSessionChange() {
  sessionRevision += 1;
  if (_refreshTimer) clearTimeout(_refreshTimer);
  _refreshTimer = null;
  _refreshPromise = null;
  return sessionRevision;
}

async function removeAuthKeys() {
  // Wait for every removal even on failure: no detached storage operation may
  // escape the serialized boundary and later overwrite the next account.
  const results = await Promise.allSettled(
    [
      "JWT",
      "REFRESH_TOKEN",
      "TrustedSession",
      "permissions",
      "hasPaymentMethodActive",
      "orgHasAvatar",
    ].map((key) => localforage.removeItem(key)),
  );
  const failure = results.find((result) => result.status === "rejected");
  if (failure?.status === "rejected") throw failure.reason;
}

export async function clearEnterpriseAuthState(expectedRevision?: number): Promise<boolean> {
  if (expectedRevision !== undefined && !isEnterpriseSessionCurrent(expectedRevision)) return false;
  return writeEnterpriseSession(beginSessionChange(), removeAuthKeys);
}

type EnterpriseSession = { jwt: string; refreshToken?: string; permissions?: unknown };

// Accept an account only while its initiating page/request is still current.
// A cancelled invite leaves neither partial credentials nor the previous owner's caches.
export async function replaceEnterpriseAuthSession(
  session: EnterpriseSession,
  isCurrent: () => boolean = () => true,
): Promise<boolean> {
  if (!isCurrent()) return false;
  const revision = beginSessionChange();
  const written = await writeEnterpriseSession(revision, async () => {
    if (!isCurrent()) return;
    await removeAuthKeys();
    if (!isCurrent() || !isEnterpriseSessionCurrent(revision)) return;
    try {
      await localforage.setItem("JWT", session.jwt);
      if (session.refreshToken) await localforage.setItem("REFRESH_TOKEN", session.refreshToken);
      await localforage.setItem("permissions", session.permissions ?? {});
      if (!isCurrent()) await removeAuthKeys();
    } catch (error) {
      await removeAuthKeys();
      throw error;
    }
  });
  const accepted = written && isCurrent();
  if (accepted) scheduleEnterpriseTokenRefresh();
  return accepted;
}

/** Refreshes only the account that initiated the request. */
export async function refreshEnterpriseSession(): Promise<boolean> {
  if (_refreshPromise) return _refreshPromise;
  const revision = sessionRevision;
  const pending = (async () => {
    try {
      await sessionWrites.catch(() => undefined);
      const refreshToken = await localforage.getItem<string>("REFRESH_TOKEN");
      if (!refreshToken || !isEnterpriseSessionCurrent(revision)) return false;
      const res = await fetch(`${cfg.REACT_APP_API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      if (typeof data.jwt !== "string" || typeof data.refreshToken !== "string") return false;
      const written = await writeEnterpriseSession(revision, async () => {
        await localforage.setItem("JWT", data.jwt);
        await localforage.setItem("REFRESH_TOKEN", data.refreshToken);
        if (data.permissions) await localforage.setItem("permissions", data.permissions);
      });
      if (written) scheduleEnterpriseTokenRefresh();
      return written;
    } catch {
      return false;
    }
  })();
  _refreshPromise = pending;
  try {
    return await pending;
  } finally {
    if (_refreshPromise === pending) _refreshPromise = null;
  }
}

/** Decodes the current JWT and refreshes five minutes before expiry. */
export function scheduleEnterpriseTokenRefresh() {
  const revision = sessionRevision;
  if (_refreshTimer) clearTimeout(_refreshTimer);
  _refreshTimer = null;
  void localforage
    .getItem("JWT")
    .then((jwt) => {
      if (!isEnterpriseSessionCurrent(revision) || !isJWTStructurallyValid(jwt)) return;
      const exp = JSON.parse(atob(jwt.split(".")[1])).exp;
      const msUntilRefresh = exp * 1000 - Date.now() - 5 * 60 * 1000;
      if (msUntilRefresh <= 0) {
        void refreshEnterpriseSession();
        return;
      }
      _refreshTimer = setTimeout(() => {
        if (isEnterpriseSessionCurrent(revision)) void refreshEnterpriseSession();
      }, msUntilRefresh);
    })
    .catch(() => undefined);
}

async function refreshPermissionsAndOrganization(
  headers: { [key: string]: string | undefined },
  revision: number,
) {
  // Payment readiness is deliberately absent here. Every gate reads
  // Enterprise's authoritative paymentMethodReady projection, which includes
  // an approved Pix Automatico authorization once the weekly rail admits it.
  // A payment-rail-specific probe must never overwrite that organization result.
  const [permissionsRes, orgAvatarRes] = await Promise.all([
    enterpriseAPI.get("/check-account", { headers }),
    enterpriseAPI.get("/has-org-avatar", { headers }).catch(() => null),
  ]);
  return writeEnterpriseSession(revision, async () => {
    await localforage.setItem("permissions", permissionsRes.data);
    await localforage.setItem("orgHasAvatar", orgAvatarRes?.data?.hasOrgAvatar === true);
  });
}

export async function checkIfCollaboratorIsAuthenticated() {
  const revision = sessionRevision;
  await sessionWrites.catch(() => undefined);
  const JWT = await localforage.getItem("JWT");
  if (!JWT || !isEnterpriseSessionCurrent(revision)) return false;
  if (!isJWTStructurallyValid(JWT)) {
    await clearEnterpriseAuthState(revision);
    return false;
  }
  const headers = { "X-JWT": JWT };
  const trustedSession = await localforage.getItem<TrustedSession>("TrustedSession");
  const isSessionFresh =
    trustedSession?.jwt === JWT && Date.now() - trustedSession.verifiedAt < SESSION_TTL_MS;
  try {
    if (!isSessionFresh || !(await localforage.getItem("permissions"))) {
      if (!(await refreshPermissionsAndOrganization(headers, revision))) return false;
    }
    const written = isSessionFresh
      ? isEnterpriseSessionCurrent(revision)
      : await writeEnterpriseSession(revision, async () => {
          await localforage.setItem("TrustedSession", { jwt: JWT, verifiedAt: Date.now() });
        });
    if (written) scheduleEnterpriseTokenRefresh();
    return written;
  } catch {
    await clearEnterpriseAuthState(revision);
    return false;
  }
}

export function Auth() {
  const [alertMessage, setAlertMessage] = React.useState("Email ou senha incorretos.");
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");

  function sendLoginRequest() {
    if (!email || !password) return;

    const revision = sessionRevision;
    enterpriseAPI
      .post("/authenticate", { email: email, password: password })
      .then(async (response) => {
        if (response.status === 200) {
          if (!isEnterpriseSessionCurrent(revision)) return;
          if (await replaceEnterpriseAuthSession(response.data)) window.location.replace("/painel");
        }
      })
      .catch((error) => {
        const msg = getUserFacingError(error, {
          context: "login",
          fallback: "Não foi possível entrar agora. Tente novamente em instantes.",
        });
        if (alertMessage !== msg) setAlertMessage(msg);
        setOpen(true);
      });
  }

  function sendOnPressEnter(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") sendLoginRequest();
  }

  return (
    <div id="authContainer">
      <div id="authInner">
        <img
          id="authLogo"
          src={`${window.location.origin}/assets/Logo_DAKASA_V1_Branco.png`}
          alt="DaKasa LTDA"
        />
        <div id="authFields">
          <BannerFeedback
            type="error"
            open={open}
            message={alertMessage}
            callback={() => setOpen(false)}
          />
          <input
            id="authEmailField"
            type="text"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={sendOnPressEnter}
          />
          <input
            id="authPasswordField"
            type="password"
            placeholder="Senha"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={sendOnPressEnter}
          />
          <button id="btnAuthLogin" onClick={sendLoginRequest}>
            Entrar <VpnKeyTwoToneIcon sx={{ fontSize: "var(--space-8)" }} />
          </button>
        </div>
      </div>
    </div>
  );
}
